import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, BookOpen, ClipboardList, MessageCircle, Inbox, LogOut, Home } from "lucide-react";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isStaff = user && (user.role === "teacher" || user.role === "admin");

  const nav = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/dashboard/courses", label: isStaff ? "Courses" : "My Courses", icon: BookOpen },
    { to: "/dashboard/assignments", label: "Assignments", icon: ClipboardList },
    { to: "/dashboard/tutor", label: "AI Tutor", icon: MessageCircle },
    ...(isStaff ? [{ to: "/dashboard/enquiries", label: "Enquiries", icon: Inbox }] : []),
  ];

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-rice flex font-sans">
      <aside className="w-60 shrink-0 bg-ink text-rice flex-col hidden md:flex sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 px-6 h-16 border-b border-white/10">
          <span className="w-8 h-8 bg-cinnabar flex items-center justify-center font-serif text-lg">中</span>
          <span className="font-display font-extrabold text-sm">CCLPT</span>
        </Link>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`side-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-cinnabar text-rice" : "text-rice/60 hover:text-rice hover:bg-white/5"}`}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-4 py-2.5 text-sm text-rice/60 hover:text-rice hover:bg-white/5 transition-colors"><Home size={17} /> Main site</Link>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rice/60 hover:text-rice hover:bg-white/5 transition-colors"><LogOut size={17} /> Log out</button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-black/10 flex items-center justify-between px-6 sticky top-0 z-20">
          <div className="md:hidden flex items-center gap-2"><span className="w-7 h-7 bg-cinnabar text-rice flex items-center justify-center font-serif">中</span><span className="font-display font-bold text-sm">CCLPT</span></div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right leading-tight hidden sm:block">
              <p className="font-display font-semibold text-sm text-ink" data-testid="user-name">{user?.name}</p>
              <p className="text-[10px] text-clay uppercase tracking-widest">{user?.role}</p>
            </div>
            {user?.picture
              ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-black/10" />
              : <div className="w-9 h-9 bg-cinnabar text-rice flex items-center justify-center font-display font-bold">{user?.name?.[0]?.toUpperCase()}</div>}
          </div>
        </header>

        {/* mobile nav */}
        <nav className="md:hidden flex overflow-x-auto bg-ink text-rice text-xs">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex-1 min-w-max flex flex-col items-center gap-1 py-2.5 px-4 ${isActive ? "bg-cinnabar" : "text-rice/60"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-5 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
